import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { loadSignees, loadDocumentEntities, loadDocumentPersons, updateDocumentSignees } from '../actionCreators';
import { shape, required, concatFailure, items, unique, errorMessage } from '@totalsoft/pure-validations';
import { emptyObject } from 'constants/emptyConst';
import DocumentSigneesComponent from './DocumentSigneesComponent';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { loadSignatureRoles, loadPartnerPositions } from 'actions/actionCreators/dictionariesActions';

const DocumentSigneesContainer = ({ documentId, siteId, showModal, setShowModal }) => {
  const signeeValidator =
    [
      unique(x => (x.partnerId || emptyObject) + (x.personId || emptyObject) + (x.positionId || emptyObject))
        |> errorMessage('OfferEdit.Documents.Signees.UniquePersons'),
      items(
        shape({
          partnerId: required,
          personId: required,
          signatureRoleId: required
        })
      )
    ] |> concatFailure;

  const key = `${documentId}-${siteId}`;
  const [documentSignees, setDocumentSignees] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const dispatch = useDispatch();
  const [validationSigneeSelection, validateSigneeSelection] = useDirtyFieldValidation(signeeValidator);

  const loaded = useSelector(state => state.document.documents.documentSignees[key]?.loaded || false, shallowEqual);
  const saving = useSelector(state => state.document.documents?.documentSignees[key]?.saving || false, shallowEqual);
  const signees = useSelector(state => (state.entities.documentSignees ? state.entities.documentSignees[key] : []), shallowEqual);
  const persons = useSelector(state => state.document.documents.documentPersons || [], shallowEqual);
  const entities = useSelector(state => state.document.documents.documentEntities[key] || {}, shallowEqual);
  const signatureRolesLoaded = useSelector(
    state => (state.dictionaries.signatureRoles ? state.dictionaries.signatureRoles.isLoaded : false),
    shallowEqual
  );
  const signatureRoles = useSelector(
    state => (state.dictionaries.signatureRoles.isLoaded ? state.dictionaries.signatureRoles.items : []),
    shallowEqual
  );
  const partnerPositionsLoaded = useSelector(
    state => (state.dictionaries.partnerPositions ? state.dictionaries.partnerPositions.isLoaded : false),
    shallowEqual
  );
  const partnerPositions = useSelector(
    state => (state.dictionaries.partnerPositions.isLoaded ? state.dictionaries.partnerPositions.items : []),
    shallowEqual
  );

  useEffect(() => {
    dispatch(loadSignees(documentId, siteId));
    dispatch(loadDocumentEntities(documentId, siteId));
  }, [documentId, siteId, dispatch, loaded]);

  useEffect(() => {
    validateSigneeSelection(documentSignees);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentSignees]);

  useEffect(() => {
    if (loaded && documentSignees.length === 0 && !isDirty) {
      setDocumentSignees(signees);
    }
  }, [documentSignees.length, isDirty, loaded, signees]);

  useEffect(() => {
    if (!signatureRolesLoaded) {
      dispatch(loadSignatureRoles());
    }
  }, [dispatch, signatureRolesLoaded]);

  useEffect(() => {
    if (!partnerPositionsLoaded) {
      dispatch(loadPartnerPositions());
    }
  }, [dispatch, partnerPositionsLoaded]);

  useEffect(() => {
    if (entities?.loaded) {
      entities.values.forEach(entity => {
        dispatch(loadDocumentPersons(documentId, siteId, entity.partnerId));
      });
    }
  }, [entities, loaded, dispatch, documentId, siteId]);

  const swapUp = useCallback(
    index => {
      let newArray = documentSignees.slice();
      let temp = newArray[index - 1];
      newArray[index - 1] = newArray[index];
      newArray[index] = temp;
      setDocumentSignees(newArray);
      setIsDirty(true);
    },
    [documentSignees]
  );

  const swapDown = useCallback(
    index => {
      let newArray = documentSignees.slice();
      let temp = newArray[index + 1];
      newArray[index + 1] = newArray[index];
      newArray[index] = temp;
      setDocumentSignees(newArray);
      setIsDirty(true);
    },
    [documentSignees]
  );

  const loadDocEntities = useCallback(() => {
    return dispatch(loadDocumentEntities(documentId, siteId)).then(data => {
      return {
        options: data.map(item => {
          return { partnerId: item.partnerId, partnerName: item.partnerName };
        })
      };
    });
  }, [dispatch, documentId, siteId]);

  const onPropertyChange = useCallback(
    (idx, propertyName) => value => {
      let newDocumentSignees = Object.assign([], documentSignees);
      if (propertyName === 'partnerId') {
        let entity = entities.values.find(x => x.partnerId === value);
        newDocumentSignees[idx]['partnerId'] = entity?.partnerId;
        newDocumentSignees[idx]['masterPartnerId'] = entity?.masterPartnerId;
        newDocumentSignees[idx]['personId'] = null;
        newDocumentSignees[idx]['personDocumentPartnerId'] = null;
      } else if (propertyName === 'personId') {
        const partnerId = newDocumentSignees[idx]['partnerId'];
        const person = persons[partnerId].values.find(x => x.personId === value);
        newDocumentSignees[idx]['personId'] = person?.personId;
        newDocumentSignees[idx]['personDocumentPartnerId'] = person?.personDocumentPartnerId;
      } else newDocumentSignees[idx][propertyName] = value;

      setDocumentSignees(newDocumentSignees);
      setIsDirty(true);
    },
    [documentSignees, entities.values, persons]
  );

  const onDelete = useCallback(
    index => {
      const newDocumentSignees = documentSignees.filter(signee => documentSignees.indexOf(signee) !== index);
      setDocumentSignees(newDocumentSignees);
      setIsDirty(true);
    },
    [documentSignees]
  );

  const addSignee = useCallback(() => {
    let newDocumentSignees = Object.assign([], documentSignees);
    newDocumentSignees.push({
      documentId: documentId,
      siteId: siteId,
      orderNo: 0,
      partnerId: null,
      personId: null,
      signatureRoleId: null
    });
    setDocumentSignees(newDocumentSignees);
    setIsDirty(true);
  }, [documentId, documentSignees, siteId]);

  const updateDocSignees = useCallback(() => {
    if (!validateSigneeSelection(documentSignees)) return;
    dispatch(updateDocumentSignees(documentId, siteId, documentSignees));
    setIsDirty(false);
  }, [dispatch, documentId, documentSignees, siteId, validateSigneeSelection]);

  const onCloseModal = useCallback(() => {
    setShowModal(false);
  }, [setShowModal]);

  return (
    <DocumentSigneesComponent
      signees={documentSignees}
      swapUp={swapUp}
      swapDown={swapDown}
      loadDocEntities={loadDocEntities}
      onDelete={onDelete}
      entities={entities}
      persons={persons}
      validation={validationSigneeSelection}
      addSignee={addSignee}
      signatureRoles={signatureRoles}
      partnerPositions={partnerPositions}
      isSaving={saving}
      isDirty={isDirty}
      onPropertyChange={onPropertyChange}
      updateDocSignees={updateDocSignees}
      onCloseModal={onCloseModal}
      open={showModal}
    />
  );
};

DocumentSigneesContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  showModal: PropTypes.bool.isRequired,
  setShowModal: PropTypes.func.isRequired
};

export default DocumentSigneesContainer;
